import React, { useCallback, useState } from 'react';
import { IconButton, InputAdornment, SxProps, Theme } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

export interface PasswordVisibilityAdornmentProps {
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  sx?: SxProps<Theme>;
  iconFontSize?: 'inherit' | 'small' | 'medium' | 'large';
  edge?: 'start' | 'end' | false;
  size?: 'small' | 'medium';
}

/**
 * Press-and-hold password visibility control.
 * Password stays visible only while the control is held (pointer or Space/Enter).
 */
export const PasswordVisibilityAdornment: React.FC<PasswordVisibilityAdornmentProps> = ({
  visible,
  onVisibleChange,
  sx,
  iconFontSize,
  edge = 'end',
  size = 'medium',
}) => {
  const show = useCallback(() => {
    onVisibleChange(true);
  }, [onVisibleChange]);

  const hide = useCallback(() => {
    onVisibleChange(false);
  }, [onVisibleChange]);

  const handlePointerDown = (event: React.PointerEvent) => {
    // Keep focus on the input; avoid sticky toggle behavior.
    event.preventDefault();
    show();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      show();
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      hide();
    }
  };

  return (
    <InputAdornment position="end">
      <IconButton
        aria-label="Mostrar contraseña mientras se mantiene pulsado"
        onPointerDown={handlePointerDown}
        onPointerUp={hide}
        onPointerLeave={hide}
        onPointerCancel={hide}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={hide}
        edge={edge}
        size={size}
        tabIndex={0}
        sx={sx}
      >
        {visible ? (
          <VisibilityOff fontSize={iconFontSize} />
        ) : (
          <Visibility fontSize={iconFontSize} />
        )}
      </IconButton>
    </InputAdornment>
  );
};

/** Convenience hook for press-and-hold visibility state. */
export function usePressAndHoldPasswordVisibility(initial = false) {
  const [visible, setVisible] = useState(initial);
  return { visible, setVisible };
}

export default PasswordVisibilityAdornment;
